import type { ReactElement, ReactNode } from 'react';

import { can, type PermissionKey } from '@/auth/permissions';
import { useAuth } from '@/auth/useAuth';

import { NotFoundPage } from './NotFoundPage';

interface RequirePermissionProps {
  readonly perm: PermissionKey;
  readonly children: ReactNode;
}

/**
 * Route-level permission gate. Per PTV2-915 the rule is "hide, don't
 * disable" — TopNav already hides the entry, but the URL is still typeable
 * (and bookmarkable), so this guards direct navigation.
 *
 * Behaviour: when the user lacks the permission we render `NotFoundPage`
 * instead of redirecting. That keeps the URL stable for bug reports and
 * prevents a route-level redirect loop if multiple guards stack later.
 */
export function RequirePermission({
  perm,
  children,
}: RequirePermissionProps): ReactElement {
  const auth = useAuth();
  if (!can(auth.user, perm)) {
    return <NotFoundPage />;
  }
  return <>{children}</>;
}
